import { createFileRoute, Link, useNavigate, useRouteContext, useSearch } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import * as v from 'valibot';
import { ArrowUpRight } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@worldcoo/ui/components/tabs';
import { FAQ_BLOCKS, getFaqEntries, type FaqBlock } from '@/content/faq';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Layout } from './-components/layout';
import { PageSeo } from './-components/page-seo';
import { FaqAccordion } from './-components/faq-accordion';
import { DiamondGrid, diamondGlows, Squiggle } from './-components/primitives';
import { slugs } from './faq.meta';

export const Route = createFileRoute('/www/faq')({
	// In the URL so support can link a section; a stale value opens the first tab rather than a 404.
	validateSearch: v.object({
		section: v.fallback(v.optional(v.picklist(FAQ_BLOCKS)), undefined),
	}),
	component: Component,
});

/** In the CATALOGS, unlike the questions: page furniture, not Comms' document. */
function useBlockTitles(): Record<FaqBlock, string> {
	const { t } = useLingui();
	return {
		companies: t`For companies`,
		ngos: t`For social entities`,
		donors: t`For donors`,
		transparency: t`Transparency and security`,
		about: t`About Worldcoo`,
	};
}

/** TEC-3821 B4/B6. Unsorted: Comms wrote it as a funnel, from funders to recipients (`content/faq.ts`). */
function Component() {
	const { t } = useLingui();
	const { locale } = useRouteContext({ from: '__root__' });
	const { section } = useSearch({ from: '/www/faq' });
	const navigate = useNavigate({ from: '/www/faq' });
	const titles = useBlockTitles();
	const entries = getFaqEntries(locale);

	const sections = FAQ_BLOCKS.map((block) => ({
		block,
		items: entries.filter((entry) => entry.block === block),
	})).filter(({ items }) => items.length > 0);

	// From the array the accordions render, as markup answering an unshown question gets the rich result pulled.
	const faqLd = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		'mainEntity': entries.map((entry) => ({
			'@type': 'Question',
			'name': entry.question,
			// One string, or the rich result quotes only the sentence introducing the list.
			'acceptedAnswer': {
				'@type': 'Answer',
				'text': [entry.answer, entry.bullets?.join(' '), entry.outro].filter(Boolean).join(' '),
			},
		})),
	};

	return (
		<Layout>
			<Header />
			<PageSeo
				slugs={slugs}
				title={t`Frequently asked questions - Worldcoo`}
				description={t`Answers to the questions companies, social entities and donors ask us most often about microdonations, the Charity Rounding Up and how the funds raised are managed.`}
				jsonLd={[faqLd]}
			/>

			<section className="bg-linen relative overflow-hidden">
				<DiamondGrid
					color="var(--color-brand)"
					opacity={0.35}
					masks={[diamondGlows({ x: '6%', y: '0%' }, { x: '94%', y: '78%' })]}
				/>

				<div className="relative mx-auto flex max-w-[1440px] flex-col items-center px-6 py-16 md:py-24">
					<div className="flex max-w-[842px] flex-col items-center gap-6 text-center">
						{/* One <Trans>: the squiggled word moves with the language ("frecuentes", "questions"). */}
						<h1 className="text-4xl font-semibold tracking-[-0.02em] md:text-5xl">
							<Trans>
								Frequently asked <Squiggle>questions</Squiggle>
							</Trans>
						</h1>
						<p className="text-ink/70 text-lg">
							{t`Everything companies, social entities and donors usually want to know before they start.`}
						</p>
					</div>

					{/* CONTROLLED, as `defaultValue` ignores a link followed from this page; `replace` spares the back button. */}
					<Tabs
						value={section ?? sections[0]?.block}
						onValueChange={(value) => {
							void navigate({ search: { section: value as FaqBlock }, replace: true });
						}}
						className="mt-10 w-full items-center gap-8 md:mt-12"
					>
						<TabsList
							variant="line"
							className="hide-scrollbar max-w-full flex-nowrap justify-start gap-2 overflow-x-auto rounded-none border-0 bg-transparent p-0 group-data-horizontal/tabs:h-auto md:justify-center"
						>
							{sections.map(({ block }) => (
								<TabsTrigger
									key={block}
									value={block}
									// The prefixes and the hover variant are not redundant: docs/notes/portals/solution-tabs.md
									className="text-ink group-data-[variant=line]/tabs-list:data-active:bg-ink group-data-[variant=line]/tabs-list:data-active:hover:bg-ink group-data-[variant=line]/tabs-list:bg-sand h-14 flex-none rounded-2xl px-6 text-base font-medium after:hidden group-data-[variant=line]/tabs-list:hover:bg-white data-active:text-white data-active:hover:text-white"
								>
									{titles[block]}
								</TabsTrigger>
							))}
						</TabsList>

						{sections.map(({ block, items }) => (
							// `forceMount`, or the served HTML shows one section while the FAQPage declares all five.
							<TabsContent
								key={block}
								value={block}
								forceMount
								className="flex w-full flex-col items-center data-[state=inactive]:hidden"
							>
								{/* The outline wants a heading the tab already gives assistive technology. */}
								<h2 className="sr-only">{titles[block]}</h2>
								<FaqAccordion items={items} />
							</TabsContent>
						))}
					</Tabs>
				</div>
			</section>

			<section className="bg-sand">
				<div className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-6 py-16 text-center md:py-24">
					<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em]">{t`Still have a question?`}</h2>
					<p className="text-ink/70 text-lg">
						{t`If you haven't found the answer you were looking for, our team will be glad to help.`}
					</p>
					<Link
						to="/www/contact"
						className="bg-ink mt-2 flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-5 font-medium text-white transition-opacity hover:opacity-90"
					>
						{t`Contact us`}
						<span className="grid size-6 place-items-center rounded-full bg-white/15">
							<ArrowUpRight className="size-4" />
						</span>
					</Link>
				</div>
			</section>

			<Footer />
		</Layout>
	);
}
